import {
	AdjustmentsHorizontalIcon,
	BanknotesIcon,
	DocumentTextIcon
} from '@heroicons/react/24/outline'
import { useContext } from 'react'
import { Controller, useWatch } from 'libs/form'
import type { FieldValues, UseFormReturn } from 'libs/form'
import { useTranslation } from 'libs/i18n'
import { ColorPicker, FormSection, Select, Switch, Textarea } from 'shared/components'
import { useFetchInfinite, useSelect } from 'shared/hooks'
import { InputGroup } from 'shared/molecules'
import { useFetchCurrencies } from 'shared/queries/useFetchCurrencies'
import { StoreSettingsContext } from 'store'
import { DEFAULT_COLOR, MAX_DESCRIPTION_LENGTH } from 'features/rentals/rentals.config'

type RentalFormSectionsProps = {
	form: UseFormReturn<FieldValues>
}

export const RentalFormSections = ({ form }: RentalFormSectionsProps) => {
	const { t } = useTranslation()
	const storeCtx = useContext(StoreSettingsContext)
	const settings = storeCtx?.storeSettings
	const {
		register,
		control,
		setValue,
		trigger,
		formState: { errors }
	} = form
	const fieldErrors = errors as any
	const description = useWatch({ control, name: 'description' })
	const { handleSelectChange } = useSelect({ setValue, trigger })
	const { currencies } = useFetchCurrencies()

	const { data: boothTypes, fetchNextPage } = useFetchInfinite({
		name: 'fetchAreaTypes',
		endpoint: '/booth-types/'
	})
	const boothTypeOptions = (boothTypes?.pages ?? [])
		.flatMap((page: any) => page.results)
		.map((boothType: any) => ({ label: boothType.name, value: boothType.pk }))
	const currencyOptions = currencies?.map((currency: any) => ({
		value: currency.value,
		label: currency.display_name
	}))

	const options = [
		{
			name: 'is_active',
			label: t('Active'),
			description: t('The rental is displayed in the booking process')
		},
		{
			name: 'product_confirm_enabled',
			label: t('Product confirmation'),
			description: t('Enable subproduct confirmation for this rental')
		},
		{
			name: 'product_import_enabled',
			label: t('Product import'),
			description: t('Enable CSV product import for this rental')
		},
		...(settings?.vat_enabled
			? [
					{
						name: 'product_vat_enabled',
						label: t('VAT selection'),
						description: t('Products on this rental can have a VAT rate')
					}
				]
			: []),
		...(settings?.supplier_sku_barcode_enabled
			? [
					{
						name: 'product_supplier_sku_barcode_enabled',
						label: t('Supplier identifiers'),
						description: t('Products on this rental can have a supplier SKU and barcode')
					}
				]
			: [])
	]

	return (
		<>
			<FormSection
				title={t('Details')}
				description={t('The name and text customers see when they book.')}
				icon={<DocumentTextIcon />}>
				<InputGroup
					id="title"
					className="sm:col-span-2"
					label={t('Title')}
					placeholder={t('e.g. Week')}
					{...register('title')}
					error={fieldErrors.title}
				/>
				<Textarea
					id="description"
					className="sm:col-span-2"
					label={t('Description')}
					letters={description?.length ?? 0}
					maxLetterCount={MAX_DESCRIPTION_LENGTH}
					{...register('description')}
					error={fieldErrors.description}
				/>
				<Controller
					name="color"
					control={control}
					render={({ field }) => (
						<ColorPicker
							id="color"
							className="sm:col-span-2"
							label={t('Color')}
							color={field.value ?? DEFAULT_COLOR}
							onChange={({ hex }) => field.onChange(hex)}
						/>
					)}
				/>
			</FormSection>

			<FormSection
				title={t('Pricing')}
				description={t('Area type, length and price of the rental.')}
				icon={<BanknotesIcon />}
				tone="success">
				<Controller
					name="booth_type"
					control={control}
					render={({ field }) => (
						<Select
							id="booth-type"
							variant="form"
							className="sm:col-span-2"
							label={t('Area type')}
							placeholder={t('Choose area type')}
							options={boothTypeOptions}
							{...field}
							handleInfiniteScroll={fetchNextPage}
						/>
					)}
				/>
				<InputGroup
					id="duration"
					type="number"
					step="1"
					label={t('Number of days')}
					placeholder="7"
					required
					{...register('duration')}
					error={fieldErrors.duration}
				/>
				<InputGroup
					id="commission"
					type="number"
					step="0.01"
					label={t('Commission (%)')}
					placeholder="0"
					{...register('commission')}
				/>
				<Controller
					name="currency"
					control={control}
					render={({ field }) => (
						<Select
							id="currency"
							variant="form"
							className="sm:col-span-2"
							label={t('Currency')}
							placeholder={t('Choose currency')}
							required
							options={currencyOptions}
							{...field}
							error={fieldErrors.currency?.value}
							onChange={(data: any) =>
								handleSelectChange({ id: 'currency', value: data?.value, label: data?.label })
							}
						/>
					)}
				/>
				<InputGroup
					id="price"
					label={t('Price')}
					placeholder="0"
					required
					{...register('price.amount')}
					error={fieldErrors.price?.amount}
				/>
				<InputGroup
					id="original_price"
					label={t('Original price')}
					placeholder="0"
					required
					{...register('original_price.amount')}
					error={fieldErrors.original_price?.amount}
				/>
			</FormSection>

			<FormSection
				title={t('Booking options')}
				description={t('What customers can do with this rental.')}
				icon={<AdjustmentsHorizontalIcon />}
				tone="warning"
				className="sm:grid-cols-1">
				{options.map((option) => (
					<Switch
						key={option.name}
						label={option.label}
						description={option.description}
						{...register(option.name)}
					/>
				))}
			</FormSection>
		</>
	)
}
